/* global React, Icon, Button, Modal, Field, Input, Select, Segmented, fmt, fmt0 */
// ============================================================
// PLUMME 3 — Privat økonomi · Scenariomotor
// Deterministisk matematikk for Fremtid-siden: fremskrivning av
// nettoformue, lånemodeller, scenariodefinisjoner og editorene
// for plan, livshendelser og mål. Ingen data — kun regnestykker
// og skjema. Brukes av privatokonomi-fremtid.jsx.
// ============================================================
const { useState: pfsUseState } = React;

// Month names come from t('po.month.N'); scenario and preset texts are getters so they follow the active language.
const PF_TONE = { neg: 'var(--error)', neutral: 'var(--ink)', pos: 'var(--success)' };
const pfsAvatar = () => (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
function pfDateFull(monthsFromNow) {
  const date = window.PoFutureMath.dateAfterMonths(monthsFromNow);
  return date ? `${t('po.month.' + (date.getMonth() + 1))} ${date.getFullYear()}` : t('pkn.fut.cannotCalcEnd');
}
function pfYearOf(monthsFromNow) {
  const date = window.PoFutureMath.dateAfterMonths(monthsFromNow);
  return date ? String(date.getFullYear()) : t('pkn.sce.notCalculable');
}
const pfProbColor = (probability) => (probability >= 80 ? 'var(--success)' : probability >= 55 ? 'var(--warning)' : 'var(--error)');
const pfGoalMonths = (goal, extraMonthly) => window.PoFutureMath.goalMonths(goal, extraMonthly);

// ---------------- Fremskrivning ----------------
function pfProject(startNet, monthly, months, annualReturn) {
  const rate = Number(annualReturn || 0) / 100 / 12;
  let value = startNet;
  const out = [value];
  for (let month = 1; month <= months; month += 1) { value = value * (1 + rate) + monthly; out.push(value); }
  return out;
}
// Full livsløpsfremskrivning: sparing frem til pensjon, uttak etter.
function pfProjectFull(startNet, baseMonthly, months, annualReturn, events, retireMonth, draw) {
  return window.PoFutureMath.project(startNet, baseMonthly, months, annualReturn, events, retireMonth, draw);
}
function pfSamplePoints(projection, months) {
  const steps = months <= 12 ? 4 : 5;
  const points = [];
  const now = new Date();
  for (let index = 0; index <= steps; index += 1) {
    const month = Math.round(months * index / steps);
    const label = months <= 12 ? (month === 0 ? t('pkn.gj.now') : `+${t('pkn.gj.monthsShort', { count: month })}`) : String(now.getFullYear() + Math.floor((now.getMonth() + month) / 12));
    points.push([label, Math.round(projection[month] || 0)]);
  }
  return points;
}
const pfAnnuity = (principal, rate, years) => {
  const monthlyRate = rate / 100 / 12;
  const n = Math.max(1, years * 12);
  if (!principal) return 0;
  if (monthlyRate === 0) return principal / n;
  return principal * monthlyRate / (1 - Math.pow(1 + monthlyRate, -n));
};
const pfSerieFirst = (principal, rate, years) => (principal ? principal / Math.max(1, years * 12) + principal * (rate / 100 / 12) : 0);
const pfMonthlyLoan = (type, principal, rate, years) => (type === 'serie' ? pfSerieFirst(principal, rate, years) : pfAnnuity(principal, rate, years));
function pfPayoffMonths(balance, rate, payment) {
  const monthlyRate = rate / 100 / 12;
  let value = balance;
  let months = 0;
  if (payment <= value * monthlyRate) return 1200;
  while (value > 0 && months < 1200) { value = value * (1 + monthlyRate) - payment; months += 1; }
  return months;
}

// ---------------- Scenarier ----------------
const PF_SCEN = [
  {
    fields: [], icon: 'sparkle', key: 'base', get label() { return t('pkn.sce.base.label'); },
    compute: (plan) => ({
      debt: null, retire: 0, save: 0, stats: [],
      story: () => t('pkn.sce.base.story', { save: fmt(plan.monthlySave), debt: fmt(plan.extraDebt) }),
    }),
  },
  {
    defaults: { extra: 3000 }, icon: 'trendUp', key: 'sparing', get label() { return t('pkn.sce.saving.label'); },
    fields: [{ k: 'extra', get label() { return t('pkn.sce.saving.field'); }, max: 20000, min: 0, step: 500, type: 'kr' }],
    compute: (plan, params) => {
      const extra = Math.max(0, Number(params.extra) || 0);
      return {
        debt: null, retire: -Math.min(4, Math.floor(extra / 2000)), save: extra,
        stats: [{ l: t('pkn.sce.saving.statExtra'), tone: 'pos', v: `+${t('pkn.fut.perMonth', { amount: fmt(extra) })}` }, { l: t('pkn.sce.saving.stat5y'), tone: 'pos', v: `+${fmt(Math.round(extra * 60 * 1.12))}` }],
        story: () => t('pkn.sce.saving.story', { amount: fmt(extra) }),
      };
    },
  },
  {
    defaults: { equity: 900000, price: 4500000, rate: 5.5, type: 'annuitet', years: 25 }, icon: 'home', key: 'bolig', get label() { return t('pkn.sce.home.label'); },
    fields: [
      { k: 'price', get label() { return t('pkn.sce.home.price'); }, max: 12000000, min: 1500000, step: 100000, type: 'kr' },
      { k: 'equity', get label() { return t('pkn.sce.equity'); }, max: 6000000, min: 0, step: 50000, type: 'kr' },
      { k: 'rate', get label() { return t('pkn.sce.rate'); }, max: 9, min: 1, step: 0.1, type: 'pct' },
      { k: 'years', get label() { return t('pkn.sce.term'); }, max: 30, min: 5, step: 1, type: 'yr' },
      { k: 'type', get label() { return t('pkn.sce.loanType'); }, options: [{ get label() { return t('pkn.sce.annuity'); }, value: 'annuitet' }, { get label() { return t('pkn.sce.serial'); }, value: 'serie' }], type: 'seg' },
    ],
    compute: (plan, params) => {
      const loan = Math.max(0, (Number(params.price) || 0) - (Number(params.equity) || 0));
      const monthly = pfMonthlyLoan(params.type, loan, Number(params.rate), Number(params.years));
      const firstInterest = loan * Number(params.rate) / 100 / 12;
      return {
        debt: `+${fmt(loan)}`, retire: 0, save: -Math.round(monthly * 0.5),
        stats: [{ l: t('pkn.sce.loan'), v: fmt(loan) }, { l: t('pkn.sce.instalment'), tone: 'neg', v: t('pkn.fut.perMonth', { amount: fmt(Math.round(monthly)) }) }, { l: t('pkn.sce.home.firstInterest'), tone: 'neg', v: t('pkn.fut.perMonth', { amount: fmt(Math.round(firstInterest)) }) }, { l: t('pkn.sce.home.value'), tone: 'pos', v: fmt(Number(params.price)) }],
        story: () => t(params.type === 'serie' ? 'pkn.sce.home.storySerial' : 'pkn.sce.home.storyAnnuity', { price: fmt(Number(params.price)), equity: fmt(Number(params.equity)), loan: fmt(loan), monthly: fmt(Math.round(monthly)) }),
      };
    },
  },
  {
    defaults: { equity: 100000, price: 450000, rate: 6.5, type: 'annuitet', years: 5 }, icon: 'car', key: 'bil', get label() { return t('pkn.sce.car.label'); },
    fields: [
      { k: 'price', get label() { return t('pkn.sce.car.price'); }, max: 1500000, min: 100000, step: 25000, type: 'kr' },
      { k: 'equity', get label() { return t('pkn.sce.equity'); }, max: 800000, min: 0, step: 25000, type: 'kr' },
      { k: 'rate', get label() { return t('pkn.sce.rate'); }, max: 12, min: 1, step: 0.1, type: 'pct' },
      { k: 'years', get label() { return t('pkn.sce.term'); }, max: 10, min: 1, step: 1, type: 'yr' },
      { k: 'type', get label() { return t('pkn.sce.loanType'); }, options: [{ get label() { return t('pkn.sce.annuity'); }, value: 'annuitet' }, { get label() { return t('pkn.sce.serial'); }, value: 'serie' }], type: 'seg' },
    ],
    compute: (plan, params) => {
      const loan = Math.max(0, (Number(params.price) || 0) - (Number(params.equity) || 0));
      const monthly = pfMonthlyLoan(params.type, loan, Number(params.rate), Number(params.years));
      return {
        debt: `+${fmt(loan)}`, retire: 0, save: -Math.round(monthly),
        stats: [{ l: t('pkn.sce.loan'), v: fmt(loan) }, { l: t('pkn.sce.instalment'), tone: 'neg', v: t('pkn.fut.perMonth', { amount: fmt(Math.round(monthly)) }) }, { l: t('pkn.sce.car.depreciation'), tone: 'neg', v: `−${fmt(Math.round(Number(params.price) * 0.45))}` }],
        story: () => t('pkn.sce.car.story', { price: fmt(Number(params.price)), equity: fmt(Number(params.equity)), loan: fmt(loan), monthly: fmt(Math.round(monthly)) }),
      };
    },
  },
  {
    defaults: { age: 62 }, icon: 'clock', key: 'pensjon', get label() { return t('pkn.sce.retire.label'); },
    fields: [{ k: 'age', get label() { return t('pkn.sce.retire.field'); }, max: 70, min: 58, step: 1, type: 'yr' }],
    compute: (plan, params) => {
      const target = Number(params.age);
      const yearsEarlier = Math.max(0, plan.retireAge - target);
      const monthsToRetire = Math.max(12, (target - plan.currentAge) * 12);
      const gap = yearsEarlier * plan.yearlyNeed;
      const required = Math.round(gap / monthsToRetire);
      return {
        debt: null, retire: -yearsEarlier, save: -required,
        stats: [{ l: t('pkn.sce.retire.statAge'), v: t('pkn.fut.ageYears', { age: target }) }, { l: t('pkn.sce.retire.statEarlier'), tone: yearsEarlier > 0 ? 'pos' : 'neutral', v: t('pkn.fut.ageYears', { age: yearsEarlier }) }, { l: t('pkn.sce.retire.statNeeded'), tone: required > 0 ? 'neg' : 'neutral', v: t('pkn.fut.perMonth', { amount: fmt(required) }) }],
        story: () => (yearsEarlier === 0
          ? t('pkn.sce.retire.storyNone', { age: target })
          : t('pkn.sce.retire.story', { age: target, years: yearsEarlier, gap: fmt(gap), required: fmt(required) })),
      };
    },
  },
  {
    defaults: { debt: 0, extra: 4000, rate: 5 }, icon: 'card', key: 'gjeld', get label() { return t('pkn.sce.debt.label'); },
    fields: [
      { k: 'debt', get label() { return t('pkn.sce.debt.today'); }, max: 6000000, min: 0, step: 50000, type: 'kr' },
      { k: 'rate', get label() { return t('pkn.sce.rate'); }, max: 14, min: 1, step: 0.1, type: 'pct' },
      { k: 'extra', get label() { return t('pkn.sce.debt.extraField'); }, max: 20000, min: 0, step: 500, type: 'kr' },
    ],
    compute: (plan, params) => {
      const balance = Math.max(0, Number(params.debt) || 0);
      const rate = Number(params.rate);
      const extra = Math.max(0, Number(params.extra) || 0);
      const basePayment = pfAnnuity(balance, rate, 25);
      const baseMonths = balance ? pfPayoffMonths(balance, rate, basePayment) : 0;
      const fastMonths = balance ? pfPayoffMonths(balance, rate, basePayment + extra) : 0;
      const yearsSaved = Math.max(0, (baseMonths - fastMonths) / 12);
      const interestSaved = Math.max(0, Math.round((basePayment * baseMonths - balance) - ((basePayment + extra) * fastMonths - balance)));
      return {
        debt: extra ? `−${t('pkn.fam.metricPerYear', { amount: fmt(extra * 12) })}` : null, retire: 0, save: -extra,
        stats: [{ l: t('pkn.sce.debt.statExtra'), tone: 'pos', v: t('pkn.fut.perMonth', { amount: fmt(extra) }) }, { l: t('pkn.sce.debt.statEarlier'), tone: yearsSaved > 0 ? 'pos' : 'neutral', v: t('pkn.sce.yearsDecimal', { years: yearsSaved.toFixed(1).replace('.', ',') }) }, { l: t('pkn.sce.debt.statInterest'), tone: 'pos', v: fmt(interestSaved) }],
        story: () => (!balance
          ? t('pkn.sce.debt.storyNone')
          : extra === 0
            ? t('pkn.sce.debt.storyNoExtra')
            : t('pkn.sce.debt.story', { extra: fmt(extra), years: yearsSaved.toFixed(1).replace('.', ','), interest: fmt(interestSaved) })),
      };
    },
  },
];

const PF_LIFE_PRESETS = [
  { duration: 216, icon: 'users', key: 'barn', monthly: -6000, get name() { return t('pkn.sce.preset.child.name'); }, get note() { return t('pkn.sce.preset.child.note'); }, oneTime: 40000, tint: 'var(--plum-600)' },
  { duration: 0, icon: 'sparkle', key: 'bryllup', monthly: 0, get name() { return t('pkn.sce.preset.wedding.name'); }, get note() { return t('pkn.sce.preset.wedding.note'); }, oneTime: 180000, tint: 'var(--plum-500)' },
  { duration: 12, icon: 'clock', key: 'permisjon', monthly: -12000, get name() { return t('pkn.sce.preset.leave.name'); }, get note() { return t('pkn.sce.preset.leave.note'); }, oneTime: 0, tint: 'var(--plum-400)' },
  { duration: 12, icon: 'sun', key: 'sabbat', monthly: -32000, get name() { return t('pkn.sce.preset.sabbatical.name'); }, get note() { return t('pkn.sce.preset.sabbatical.note'); }, oneTime: 0, tint: 'var(--warning)' },
  { duration: 0, icon: 'trendDown', key: 'deltid', monthly: -9000, get name() { return t('pkn.sce.preset.partTime.name'); }, get note() { return t('pkn.sce.preset.partTime.note'); }, oneTime: 0, tint: 'var(--warning)' },
  { duration: 0, icon: 'trendUp', key: 'lonn', monthly: 5000, get name() { return t('pkn.sce.preset.raise.name'); }, get note() { return t('pkn.sce.preset.raise.note'); }, oneTime: 0, tint: 'var(--success)' },
  { duration: 0, icon: 'home', key: 'oppussing', monthly: 0, get name() { return t('pkn.sce.preset.renovation.name'); }, get note() { return t('pkn.sce.preset.renovation.note'); }, oneTime: 250000, tint: 'var(--plum-700)' },
  { duration: 0, icon: 'wallet', key: 'arv', monthly: 0, get name() { return t('pkn.sce.preset.inheritance.name'); }, get note() { return t('pkn.sce.preset.inheritance.note'); }, oneTime: -300000, tint: 'var(--success)' },
];

// ---------------- Graf ----------------
function PoLineChart({ points }) {
  const [hover, setHover] = pfsUseState(null);
  const width = 860;
  const height = 300;
  const pad = { bottom: 34, left: 16, right: 16, top: 22 };
  const innerWidth = width - pad.left - pad.right;
  const innerHeight = height - pad.top - pad.bottom;
  const max = Math.max(1, ...points.map((point) => point[1])) * 1.12;
  const x = (index) => pad.left + (points.length === 1 ? innerWidth / 2 : innerWidth * index / (points.length - 1));
  const y = (value) => pad.top + innerHeight * (1 - value / max);
  const coords = points.map((point, index) => [x(index), y(point[1])]);
  const line = coords.map((point, index) => `${index ? 'L' : 'M'}${point[0].toFixed(1)} ${point[1].toFixed(1)}`).join(' ');
  const area = `${line} L ${x(points.length - 1).toFixed(1)} ${y(0).toFixed(1)} L ${x(0).toFixed(1)} ${y(0).toFixed(1)} Z`;
  const ticks = [0, max * 0.5, max].map((value) => Math.round(value));
  return <div className="pf-chart-wrap">
    <svg viewBox={`0 0 ${width} ${height}`} width="100%" height="100%" preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs><linearGradient id="poCfFill" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="var(--plum-400)" stopOpacity="0.34" /><stop offset="100%" stopColor="var(--plum-400)" stopOpacity="0.02" />
      </linearGradient></defs>
      {ticks.map((tick, index) => <g key={index}>
        <line x1={pad.left} x2={width - pad.right} y1={y(tick)} y2={y(tick)} stroke="var(--border)" strokeWidth="1" />
        <text x={pad.left + 2} y={y(tick) - 6} fontSize="12" fontWeight="700" fill="var(--muted-2)">{fmt0(tick)}</text>
      </g>)}
      <path d={area} fill="url(#poCfFill)" />
      <path d={line} fill="none" stroke="var(--plum-600)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
      {coords.map((point, index) => <g key={index}>
        {hover === index && <line x1={point[0]} x2={point[0]} y1={pad.top} y2={y(0)} stroke="var(--plum-300)" strokeWidth="1.5" strokeDasharray="4 4" />}
        <circle cx={point[0]} cy={point[1]} r={hover === index ? 7 : 4.5} fill="var(--plum-600)" stroke="var(--card)" strokeWidth="2" style={{ transition: 'r .12s' }} />
        <text x={point[0]} y={height - 13} fontSize="12.5" fontWeight={hover === index ? 900 : 700} fill={hover === index ? 'var(--plum-700)' : 'var(--muted)'} textAnchor="middle">{points[index][0]}</text>
      </g>)}
      {coords.map((point, index) => {
        const from = index === 0 ? 0 : (coords[index - 1][0] + point[0]) / 2;
        const to = index === coords.length - 1 ? width : (coords[index + 1][0] + point[0]) / 2;
        return <rect data-app-control="true" key={`hit-${index}`} x={from} y={0} width={to - from} height={height} fill="transparent" style={{ cursor: 'pointer' }}
          onMouseEnter={() => setHover(index)} onMouseLeave={() => setHover(null)} onClick={() => setHover(hover === index ? null : index)} />;
      })}
    </svg>
    {hover !== null && <div className="pf-tip" style={{ left: `${Math.max(9, Math.min(91, coords[hover][0] / width * 100))}%`, top: `${coords[hover][1] / height * 100}%`, transform: coords[hover][1] < height * 0.34 ? 'translate(-50%, 14px)' : 'translate(-50%, calc(-100% - 16px))' }}>
      <div className="pf-tip-x">{points[hover][0]}</div>
      <div className="pf-tip-v">{fmt(points[hover][1])}</div>
    </div>}
  </div>;
}

// ---------------- Editorer ----------------
function PoPlanEditor({ plan, onClose, onSave }) {
  const [draft, setDraft] = pfsUseState({ ...plan });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const valid = Number(draft.currentAge) >= 18 && Number(draft.currentAge) <= 100
    && Number(draft.retireAge) >= 40 && Number(draft.retireAge) <= 100
    && Number(draft.retireAge) > Number(draft.currentAge)
    && draft.ret !== '' && Number.isFinite(Number(draft.ret));
  const save = () => onSave({
    ...plan,
    currentAge: Math.max(18, Math.min(100, Math.round(Number(draft.currentAge)))),
    drawMonthly: Math.max(0, Math.round(Number(draft.drawMonthly) || 0)),
    extraDebt: Math.max(0, Math.round(Number(draft.extraDebt) || 0)),
    monthlySave: Math.max(0, Math.round(Number(draft.monthlySave) || 0)),
    ret: Math.max(0, Math.min(15, Number(draft.ret) || 0)),
    retireAge: Math.max(40, Math.min(100, Math.round(Number(draft.retireAge)))),
    targetNet: Math.max(0, Math.round(Number(draft.targetNet) || 0)),
  });
  return <Modal open onClose={onClose} max={520} title={t('po.todaysPlan')}
    footer={<React.Fragment><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button><Button disabled={!valid} onClick={save} data-help={t('helpTopics.scenario.body')} data-help-title={t('helpTopics.scenario.title')}>{t('po.savePlan')}</Button></React.Fragment>}>
    <div className="pf-form">
      <div className="pf-plan-info"><Icon name="sparkle" size={16} /><p>{t('pkn.sce.planInfo')}</p></div>
      <Field label={t('po.monthlySaving')} hint={t('pkn.sce.hintMonthlySave')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.monthlySave} onChange={(event) => set('monthlySave', event.target.value)} placeholder="0" /></Field>
      <Field label={t('po.monthlyExtraDebt')} hint={t('pkn.sce.hintExtraDebt')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.extraDebt} onChange={(event) => set('extraDebt', event.target.value)} placeholder="0" /></Field>
      <Field label={t('po.targetNetWealth')} hint={t('pkn.sce.hintTarget')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.targetNet} onChange={(event) => set('targetNet', event.target.value)} placeholder="0" /></Field>
      <div className="pf-form-row">
        <Field label={t('po.expectedReturn')} hint={t('pkn.sce.hintReturn')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.ret} onChange={(event) => set('ret', event.target.value)} placeholder="0" /></Field>
        <Field label={t('po.yourAgeToday')}><Input type="number" value={draft.currentAge} onChange={(event) => set('currentAge', event.target.value)} placeholder={t('pkn.sce.phAge')} /></Field>
      </div>
      <Field label={t('po.desiredRetirementAge')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.retireAge} onChange={(event) => set('retireAge', event.target.value)} placeholder={t('pkn.sce.phDesiredAge')} /></Field>
      <Field label={t('pkn.sce.pensionSpend')} hint={t('pkn.sce.pensionSpendHint')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.drawMonthly} onChange={(event) => set('drawMonthly', event.target.value)} placeholder="0" /></Field>
    </div>
  </Modal>;
}

function PoScenControls({ definition, onChange, params }) {
  const format = (field, value) => (field.type === 'kr' ? fmt(Number(value)) : field.type === 'pct' ? `${String(value).replace('.', ',')} %` : field.type === 'yr' ? t('pkn.fut.ageYears', { age: value }) : value);
  return <div className="pf-params">
    {definition.fields.map((field) => (field.type === 'seg'
      ? <div key={field.k} className="pf-param pf-param-seg" data-help={t('helpTopics.repaymentType.body')} data-help-title={t('helpTopics.repaymentType.title')}>
        <div className="pf-param-head"><span className="pf-param-lbl">{field.label}</span></div>
        <Segmented value={params[field.k]} onChange={(value) => onChange(field.k, value)} options={field.options} />
      </div>
      : <div key={field.k} className="pf-param" data-help={t('helpTopics.' + (field.k === 'rate' ? 'interestRate' : field.k === 'years' ? 'loanTerms' : 'scenario') + '.body')} data-help-title={field.label}>
        <div className="pf-param-head"><span className="pf-param-lbl">{field.label}</span><span className="pf-param-val">{format(field, params[field.k])}</span></div>
        <input className="pf-range" type="range" min={field.min} max={field.max} step={field.step} value={params[field.k]}
          onChange={(event) => onChange(field.k, field.step % 1 === 0 ? parseInt(event.target.value, 10) : parseFloat(event.target.value))} />
      </div>))}
  </div>;
}

function PoLifeEventEditor({ event, onClose, onSave }) {
  const isNew = !event || event._new;
  const [draft, setDraft] = pfsUseState(() => {
    const source = isNew ? {} : event;
    return { duration: source.duration ?? 0, icon: source.icon || 'sparkle', key: source.key || '', monthly: source.monthly ?? 0, name: source.name || '', note: source.note || '', oneTime: source.oneTime ?? 0, start: source.start ?? 12, tint: source.tint || 'var(--plum-600)' };
  });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const pickPreset = (preset) => setDraft((current) => ({ ...current, duration: preset.duration, icon: preset.icon, key: preset.key, monthly: preset.monthly, name: preset.name, note: preset.note, oneTime: preset.oneTime, tint: preset.tint }));
  const save = () => onSave({
    active: event ? event.active !== false : true,
    duration: Math.max(0, Math.round(Number(draft.duration) || 0)),
    icon: draft.icon,
    id: (event && event.id) || `ev${Date.now()}`,
    key: draft.key,
    monthly: Math.round(Number(draft.monthly) || 0),
    name: draft.name.trim(),
    note: draft.note,
    oneTime: Math.round(Number(draft.oneTime) || 0),
    start: Math.max(0, Math.round(Number(draft.start) || 0)),
    tint: draft.tint,
  });
  return <Modal open onClose={onClose} max={560} title={isNew ? t('pkn.sce.newEvent') : t('pkn.sce.editEvent')}
    footer={<React.Fragment><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button><Button disabled={!draft.name.trim()} onClick={save}>{isNew ? t('pkn.sce.add') : t('pkn.sce.saveChanges')}</Button></React.Fragment>}>
    <div className="pf-form">
      {isNew && <Field label={t('po.pickEvent')}><div className="pf-life-presets">
        {PF_LIFE_PRESETS.map((preset) => <button key={preset.key} type="button" className={`pf-life-preset${draft.key === preset.key ? ' on' : ''}`} onClick={() => pickPreset(preset)}>
          <span className="pf-life-preset-ic" style={{ color: preset.tint }}><Icon name={preset.icon} size={17} /></span>{preset.name}
        </button>)}
      </div></Field>}
      <Field label={t('cust.form.contactName')}><Input value={draft.name} onChange={(event2) => set('name', event2.target.value)} placeholder={t('po.eventNamePh')} /></Field>
      <div className="pf-param" data-help={t('helpTopics.plannedCash.body')} data-help-title={t('po.starts')}>
        <div className="pf-param-head"><span className="pf-param-lbl">{t('po.starts')}</span><span className="pf-param-val">{pfDateFull(draft.start)}</span></div>
        <input className="pf-range" type="range" min={0} max={180} step={1} value={draft.start} onChange={(event2) => set('start', parseInt(event2.target.value, 10))} />
      </div>
      <div className="pf-form-row">
        <Field label={t('po.oneOffCost')} hint={t('pkn.sce.hintOneOff')}><Input type="number" value={draft.oneTime} onChange={(event2) => set('oneTime', event2.target.value)} placeholder="0" /></Field>
        <Field label={t('po.monthlyEffect')} hint={t('pkn.sce.hintMonthlyEffect')}><Input type="number" value={draft.monthly} onChange={(event2) => set('monthly', event2.target.value)} placeholder="0" /></Field>
      </div>
      <Field label={t('po.durationMonths')} hint={t('pkn.sce.hintDuration')}><Input type="number" value={draft.duration} onChange={(event2) => set('duration', event2.target.value)} placeholder="0" /></Field>
    </div>
  </Modal>;
}

function pfBuildGoal(form, existing) {
  const saved = Math.max(0, Number(form.saved) || 0);
  const target = Math.max(1, Number(form.target) || 0);
  const monthly = Math.max(0, Number(form.monthly) || 0);
  const nextAt = saved >= target ? target : Math.min(target, Math.max(saved + 1000, Math.ceil((saved + (target - saved) * 0.4) / 1000) * 1000));
  const name = form.name.trim() || t('pkn.sce.newGoalDefault');
  return {
    accountId: form.accountId || '',
    icon: form.icon || 'sparkle',
    key: (existing && existing.key) || `g${Date.now()}`,
    lever: monthly > 0 ? t('pkn.sce.leverFixed') : t('pkn.sce.leverSetUp'),
    monthly,
    name,
    nextMark: { at: nextAt, label: t('pkn.sce.goalPasses', { name, amount: fmt(nextAt) }) },
    note: form.note.trim(),
    saved,
    target,
    tint: form.tint || 'var(--plum-600)',
  };
}

function PoGoalEditor({ accounts = [], goal, onClose, onSave }) {
  const isNew = !goal || goal._new;
  const source = isNew ? {} : goal;
  const [draft, setDraft] = pfsUseState({ accountId: source.accountId || '', icon: source.icon || 'sparkle', monthly: source.monthly || '', name: source.name || '', note: source.note || '', saved: source.saved || '', target: source.target || '', tint: source.tint || 'var(--plum-600)' });
  const set = (key, value) => setDraft((current) => ({ ...current, [key]: value }));
  const icons = ['wallet', 'home', 'car', 'sun', 'clock', 'trendUp', 'sparkle', 'checkCircle'];
  const tints = ['var(--plum-700)', 'var(--plum-600)', 'var(--plum-500)', 'var(--plum-400)', 'var(--success)', 'var(--warning)'];
  const valid = draft.name.trim() && Number(draft.target) > 0;
  return <Modal open onClose={onClose} max={520} title={isNew ? t('pkn.sce.newGoal') : t('pkn.sce.editGoal')}
    footer={<React.Fragment><Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button><Button disabled={!valid} onClick={() => onSave(pfBuildGoal(draft, isNew ? null : goal))}>{isNew ? t('pkn.sce.createGoal') : t('pkn.sce.saveChanges')}</Button></React.Fragment>}>
    <div className="pf-form">
      <Field label={t('po.goalName')}><Input value={draft.name} onChange={(event) => set('name', event.target.value)} placeholder={t('po.goalNamePh')} /></Field>
      <Field label={t('po.shortDescription')}><Input value={draft.note} onChange={(event) => set('note', event.target.value)} placeholder={t('po.whatSavingFor')} /></Field>
      <Field label={t('pkn.sce.linkSavings')} hint={t('pkn.sce.linkSavingsHint')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}><Select value={draft.accountId} onChange={(event) => set('accountId', event.target.value)}><option value="">{t('pkn.sce.noAccount')}</option>{accounts.filter((account) => account.kind !== 'credit-card').map((account) => <option key={account.id} value={account.id}>{account.name}</option>)}</Select></Field>
      <div className="pf-form-row">
        <Field label={t('po.targetAmount')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}><Input type="number" value={draft.target} onChange={(event) => set('target', event.target.value)} placeholder="0" /></Field>
        <Field label={t('po.savedSoFar')} help={t('helpTopics.goal.body')} helpTitle={t('helpTopics.goal.title')}><Input disabled={Boolean(draft.accountId)} type="number" value={draft.saved} onChange={(event) => set('saved', event.target.value)} placeholder="0" /></Field>
      </div>
      <Field label={t('po.monthlySaving')} help={t('helpTopics.scenario.body')} helpTitle={t('helpTopics.scenario.title')}><Input type="number" value={draft.monthly} onChange={(event) => set('monthly', event.target.value)} placeholder="0" /></Field>
      <Field label={t('po.icon')}><div className="pf-pick">{icons.map((icon) => <button key={icon} type="button" className={`pf-pick-ic${draft.icon === icon ? ' on' : ''}`} onClick={() => set('icon', icon)}><Icon name={icon} size={18} /></button>)}</div></Field>
      <Field label={t('po.colour')}><div className="pf-pick">{tints.map((tint) => <button key={tint} type="button" className={`pf-pick-color${draft.tint === tint ? ' on' : ''}`} style={{ background: tint }} onClick={() => set('tint', tint)} aria-label={t('po.pickColour')} />)}</div></Field>
    </div>
  </Modal>;
}

Object.assign(window, { PF_LIFE_PRESETS, PF_SCEN, PF_TONE, PoGoalEditor, PoLifeEventEditor, PoLineChart, PoPlanEditor, PoScenControls, pfBuildGoal, pfDateFull, pfGoalMonths, pfProbColor, pfProject, pfProjectFull, pfSamplePoints, pfYearOf });
